import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { ApiError, admin, setDeniedHandler, type AdminMe } from '../lib/api';

/**
 * Admin session state.
 *
 * Four states rather than two. `authenticated` — a valid session with no platform roles
 * — is distinct from `anonymous`, because the two need different messages: one person
 * needs to sign in, the other needs to be told they are not staff and stop trying.
 */
export type AdminStatus = 'unknown' | 'staff' | 'authenticated' | 'anonymous';

export const useAdminSession = defineStore('admin-session', () => {
  const status = ref<AdminStatus>('unknown');
  const me = ref<AdminMe | null>(null);

  const displayName = computed(
    () => me.value?.user.display_name ?? me.value?.user.email?.split('@')[0] ?? 'operator',
  );
  const roles = computed(() => me.value?.platform_roles ?? []);
  const isAdmin = computed(() => roles.value.includes('platform.admin'));

  /**
   * In-flight refresh, shared by concurrent callers.
   *
   * The router guard and the root component both resolve the session on first load.
   * Without this, a slow response means both fire and the app makes two identical
   * requests before it has rendered anything.
   */
  let inFlight: Promise<void> | null = null;

  async function refresh(): Promise<void> {
    if (inFlight !== null) return inFlight;
    inFlight = doRefresh().finally(() => {
      inFlight = null;
    });
    return inFlight;
  }

  async function doRefresh(): Promise<void> {
    try {
      const profile = await admin.me();
      me.value = profile;
      status.value = profile.is_staff ? 'staff' : 'authenticated';
    } catch (error) {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        me.value = null;
        status.value = 'anonymous';
      }
    }
  }

  async function signIn(email: string, password: string): Promise<void> {
    await admin.login(email, password);
    await refresh();
  }

  async function signOut(): Promise<void> {
    try {
      await admin.logout();
    } finally {
      me.value = null;
      status.value = 'anonymous';
    }
  }

  setDeniedHandler(() => {
    if (status.value === 'staff') status.value = 'anonymous';
  });

  return { status, me, displayName, roles, isAdmin, refresh, signIn, signOut };
});
